import { h, mount } from "@/lib/dom";
import {
  loading,
  emptyState,
  button,
  toast,
  textInput,
  select,
  confirmDialog,
  openModal,
  field,
} from "@/components/ui";
import { icon } from "@/lib/icons";
import { navigate, type RouteContext } from "@/app/router";
import {
  subjectsService,
  timetableService,
  topicsService,
  homeworkService,
  holidaysService,
  findHoliday,
  pbErrorMessage,
  type HomeworkSuggestion,
  type Holiday,
} from "@/services";
import { examTermField } from "@/features/topics/examTermField";
import { deleteHomework } from "@/features/homework/fromNotes";
import { chooseTopic } from "@/features/topics/topicPicker";
import { bindTaskCheckboxes } from "@/markdown/tasks";
import { renderPreview } from "@/markdown/preview";
import { SLASH_CMDS, attachSlashMenu, insertAtCursor, insertTermDef, pickImage } from "@/features/editor/slash";
import { attachMarkdownKeys } from "@/features/editor/keys";
import { textareaField } from "@/features/editor/field";
import { openTableEditor, findTableAt } from "@/features/editor/tableEditor";
import { defaultColor } from "@/lib/colors";
import {
  WEEKDAY_SHORT,
  addDays,
  buildBlocks,
  currentSlot,
  homeworkReferencePeriod,
  formatShortDate,
  toLocalISODate,
  upcomingSubjectSlots,
  weekdayOf,
} from "@/features/timetable/schedule";
import { daysUntil, formatCountdown } from "@/lib/format";
import { voiceControl } from "@/features/voice/voicePanel";
import { offlineSync } from "@/services/offline/offline-sync.service";
import { checkConnectivity, connectivityMessage } from "@/services/offline/connectivity";
import type { Homework, Period, Subject, TimetableSlot, Topic, Weekday } from "@/types/models";
import { openTreeFor } from "@/features/editor/treeInsert";

const LAST_SUBJECT_KEY = "lernapp.homework.lastSubject";

/**
 * Fächer des heutigen Tages in Vorschlagsreihenfolge: die laufende Stunde
 * zuerst, dann die davor, dann die danach – also nach Abstand zur aktuellen
 * Stunde, bei Gleichstand hat die frühere Vorrang. Die neue Stunde zählt erst
 * `SWITCH_GRACE_MIN` Minuten nach ihrem Beginn (siehe `homeworkReferencePeriod`).
 */
export function todaySubjectIds(slots: TimetableSlot[], periods: Period[]): string[] {
  const blocks = buildBlocks(slots, weekdayOf(new Date()), periods.length);
  if (!blocks.length) return [];

  const ref = homeworkReferencePeriod(blocks, periods);

  const distance = (from: number, span: number) => {
    const to = from + span - 1;
    if (from <= ref && ref <= to) return 0;
    return Math.min(Math.abs(from - ref), Math.abs(to - ref));
  };

  return [...blocks]
    .sort(
      (a, b) =>
        distance(a.from, a.span) - distance(b.from, b.span) ||
        (a.from < ref ? 0 : 1) - (b.from < ref ? 0 : 1) ||
        a.from - b.from
    )
    .map((b) => b.subject)
    .filter((id): id is string => !!id)
    .filter((id, i, arr) => arr.indexOf(id) === i);
}

/** Frist als kurzer Text, z. B. „Fr 01.08. · 3. Stunde". */
export function dueLabel(hw: Pick<Homework, "dueDate" | "duePeriod">): string {
  if (!hw.dueDate) return "";
  const d = new Date(`${hw.dueDate}T00:00:00`);
  const day = `${WEEKDAY_SHORT[weekdayOf(d)]} ${formatShortDate(d)}`;
  return hw.duePeriod ? `${day} · ${hw.duePeriod}. Stunde` : day;
}

/** Hausaufgaben: Schnellanlage oben, darunter die Aufgaben nach Frist-Tag. */
export function HomeworkPage(ctx: RouteContext): HTMLElement {
  const container = h("div", null);
  // `?fach=<subjectId>`: Filter statt eines eigenen Popups (früher öffnete
  // der runde Zähler auf der Themenseite ein Popup, siehe list.ts) – der
  // Filter steht in der Adresse, ist also auch per Link erreichbar/teilbar.
  const filterSubjectId = ctx.query.get("fach") || undefined;

  let subjects: Subject[] = [];
  let topics: Topic[] = [];
  let todayIds: string[] = [];
  let items: Homework[] = [];
  let ttSlots: TimetableSlot[] = [];
  let ttPeriods: Period[] = [];
  let ttDays: Weekday[] = [];
  let holidays: Holiday[] = [];
  let initialized = false;
  // Die Schnellanlage wird nur einmal gebaut und danach wiederverwendet –
  // sonst ginge bei jedem Neuzeichnen die Eingabe verloren und das
  // Frist-Feld würde den Stundenplan erneut laden.
  let createEl: HTMLElement | null = null;

  async function load(): Promise<void> {
    if (!initialized) mount(container, loading());
    try {
      const now = new Date();
      await subjectsService.ensureSonstiges().catch(() => {});
      const [subs, slots, settings, list, allTopics, hols] = await Promise.all([
        subjectsService.list(),
        timetableService.listSlots(),
        timetableService.getSettings(),
        homeworkService.listAll(),
        topicsService.listAll().catch(() => [] as Topic[]),
        holidaysService.forRange(now, addDays(now, 120)).catch(() => [] as Holiday[]),
      ]);
      subjects = subs;
      ttSlots = slots;
      ttPeriods = settings.periods;
      ttDays = settings.days;
      holidays = hols;
      todayIds = todaySubjectIds(slots, settings.periods);
      if (!subjects.length) createEl = null;
      items = list;
      topics = allTopics;
      initialized = true;
      render();
    } catch (err) {
      initialized = true;
      const [cachedSubjects, cachedTopics, cachedHomework] = await Promise.all([
        offlineSync.loadSubjects(),
        offlineSync.loadTopics(),
        offlineSync.loadHomeworkList(),
      ]);
      if (cachedSubjects && cachedHomework) {
        subjects = cachedSubjects;
        topics = cachedTopics ?? [];
        items = cachedHomework;
        ttSlots = [];
        ttPeriods = [];
        ttDays = [];
        holidays = [];
        todayIds = [];
        render();
        toast("Offline – zeige zwischengespeicherten Stand.", "warning");
        return;
      }
      const status = await checkConnectivity();
      mount(
        container,
        h(
          "div",
          { class: "card card-pad" },
          emptyState({
            icon: "checklist",
            title: "Hausaufgaben konnten nicht geladen werden",
            text: status.backendReachable ? pbErrorMessage(err) : connectivityMessage(status),
            action: button({ label: "Erneut versuchen", onClick: () => void load() }),
          })
        )
      );
    }
  }
  const refresh = () => void load();
  const subjectById = (id: string) => subjects.find((s) => s.id === id);
  /** Eingabefeld der Schnellanlage – bekommt beim ersten Zeichnen den Fokus. */
  let focusField: HTMLInputElement | null = null;
  let focused = false;

  /** Vorgeschlagene Fächer (heute) oben, darunter alle. */
  function subjectSelect(value: string, onchange: (id: string) => void) {
    const opt = (s: Subject) => ({ value: s.id, label: s.title });
    const today = todayIds.map(subjectById).filter((s): s is Subject => Boolean(s));
    const el = select({
      groups: [
        { label: "Heute", options: today.map(opt) },
        { label: "Alle Fächer", options: subjects.map(opt) },
      ],
      value,
      onchange: () => onchange(el.value),
    });
    return el;
  }

  /* ---------- Mikro-Erfassung ---------- */

  let voicePanelEl: HTMLElement | null = null;

  interface Candidate {
    date: string;
    periodFrom: number;
    periodTo: number;
  }
  /** Zuletzt berechnete Kandidaten je Fach – von `buildVoiceContext` befüllt, von `resolveCandidateIndex` gelesen. */
  let subjectCandidates: Record<string, Candidate[]> = {};

  function buildVoiceContext(): unknown {
    const today = new Date();
    const candidates: Record<string, Candidate[]> = {};
    for (const s of subjects) {
      const upcoming = upcomingSubjectSlots({
        slots: ttSlots,
        periods: ttPeriods,
        days: ttDays,
        subjectId: s.id,
        from: today,
        weeksAhead: 3,
      })
        .filter((slot) => !findHoliday(holidays, toLocalISODate(slot.date)))
        .slice(0, 5)
        .map((slot) => ({ date: toLocalISODate(slot.date), periodFrom: slot.from, periodTo: slot.to }));
      if (upcoming.length) candidates[s.id] = upcoming;
    }
    subjectCandidates = candidates;

    const hit = currentSlot(ttSlots, ttPeriods);
    return {
      subjects: subjects.map((s) => ({ id: s.id, title: s.title })),
      topics: topics.map((t) => ({ id: t.id, title: t.title, subjectId: t.subject, state: t.state })),
      currentSubjectId: todaySubjectIds(ttSlots, ttPeriods)[0] ?? hit?.slot.subject ?? null,
      lastSubjectId: localStorage.getItem(LAST_SUBJECT_KEY),
      // Je Kandidat sein Index mitgeben: die KI soll nur diese Zahl
      // zurückgeben (candidateIndex), nicht selbst mit Daten rechnen.
      candidates: Object.fromEntries(
        Object.entries(candidates).map(([id, list]) => [id, list.map((c, i) => ({ index: i, ...c }))])
      ),
      today: toLocalISODate(today),
    };
  }

  /** Löst `candidateIndex` (KI hat eine Stunde aus der Liste gewählt) in echtes Datum + Stunde auf. */
  function resolveCandidateIndex(p: HomeworkSuggestion): HomeworkSuggestion {
    if (p.candidateIndex == null) return p;
    const candidate = subjectCandidates[p.subject]?.[p.candidateIndex];
    if (!candidate) return p;
    return { ...p, dueDate: candidate.date, duePeriod: candidate.periodFrom, candidateIndex: undefined };
  }

  async function applyVoiceHomework(p: HomeworkSuggestion): Promise<void> {
    if (!p.subject || !subjectById(p.subject)) throw new Error("Unbekanntes Fach – bitte Fach wählen.");
    if (!p.dueDate) throw new Error("Bitte eine Frist wählen.");
    const topic = await topicsService.currentBySubject(p.subject).catch(() => undefined);
    await homeworkService.create({
      subject: p.subject,
      topic: topic?.id,
      text: p.text ?? "",
      dueDate: p.dueDate,
      duePeriod: p.duePeriod,
    });
    localStorage.setItem(LAST_SUBJECT_KEY, p.subject);
  }

  function voiceHomeworkPanel(): HTMLElement {
    voicePanelEl ??= voiceControl<HomeworkSuggestion>({
      domain: "homework",
      buildContext: buildVoiceContext,
      applyOne: applyVoiceHomework,
      normalize: resolveCandidateIndex,
      onApplied: refresh,
      columns: [
        {
          label: "Fach",
          render: (p, patch) => subjectSelect(p.subject, (id) => patch({ subject: id, subjectTitle: subjectById(id)?.title })),
        },
        {
          label: "Aufgabe",
          render: (p, patch) => {
            const el = textInput({ value: p.text ?? "", placeholder: "Aufgabe (optional)" });
            el.addEventListener("input", () => patch({ text: el.value }));
            return el;
          },
        },
        {
          label: "Frist",
          render: (p, patch) => {
            const el = h("input", { type: "date", class: "input", value: p.dueDate ?? "" }) as HTMLInputElement;
            el.addEventListener("input", () => patch({ dueDate: el.value }));
            return el;
          },
        },
      ],
    });
    return voicePanelEl;
  }

  /* ---------- Schnellanlage ---------- */

  function createCard(): HTMLElement {
    if (!subjects.length) {
      return h(
        "div",
        { class: "card card-pad" },
        h("p", { class: "muted" }, "Lege zuerst ein Fach an – dann kannst du hier Hausaufgaben erfassen.")
      );
    }

    const first = (filterSubjectId && subjectById(filterSubjectId) ? filterSubjectId : undefined) ?? todayIds[0] ?? subjects[0].id;
    // "Sonstiges" ist der Sammeltopf für Orga-Kram ohne echte Unterrichtsstunde
    // – dafür macht die Stunden-Auswahl keinen Sinn, immer direkt ein Datum.
    const isSonstiges = (id: string) => subjectById(id)?.title.trim().toLowerCase() === "sonstiges";
    const textField = textInput({ placeholder: "Was ist zu tun?" });
    // Direkt lostippen können, ohne erst ins Feld zu klicken.
    focusField = textField;
    const picker = subjectSelect(first, (id) => {
      termField.setSubject(id);
      termField.setMode(isSonstiges(id) ? "date" : "slot");
    });
    // Voreinstellung: nächste Stunde des Fachs ab morgen, also an einem
    // anderen Tag als heute.
    const termField = examTermField({
      subjectId: first,
      from: addDays(new Date(), 1),
      defaultMode: isSonstiges(first) ? "date" : "slot",
      autoSelect: true,
    });
    const addBtn = button({ icon: "plus", label: "Anlegen", variant: "primary", onClick: () => void create() });

    async function create(): Promise<void> {
      const text = textField.value.trim();
      const term = termField.getValue();
      if (!term.date) return toast("Bitte eine Frist wählen.", "error");
      addBtn.disabled = true;
      try {
        const subject = subjectById(picker.value);
        // Thema ermitteln: wählt der Nutzer bei mehreren aktiven selbst, bei
        // genau einem wird es still gesetzt, bei keinem currentBySubject als
        // Fallback – so landet die Hausaufgabe immer mit topicId in PocketBase
        // und ist auf der Themenseite im Hausaufgaben-Popup sichtbar.
        let topic = subject
          ? await chooseTopic({
              subject,
              topics: topics.filter((t) => t.subject === subject.id),
              title: "Zu welchem Thema?",
            })
          : undefined;
        if (!topic && subject) {
          topic = await topicsService.currentBySubject(subject.id).catch(() => undefined);
        }
        await homeworkService.create({
          subject: picker.value,
          topic: topic?.id,
          text,
          dueDate: term.date,
          duePeriod: term.periodFrom,
        });
        localStorage.setItem(LAST_SUBJECT_KEY, picker.value);
        textField.value = "";
        refresh();
      } catch (err) {
        toast(pbErrorMessage(err), "error");
      } finally {
        addBtn.disabled = false;
      }
    }

    textField.addEventListener("keydown", (e: KeyboardEvent) => {
      if (e.key === "Enter") void create();
    });

    return h(
      "div",
      { class: "card card-pad hw-new" },
      textField,
      picker,
      termField.el,
      addBtn
    );
  }

  /* ---------- Kompaktzeile ---------- */

  let showDone = false;

  /**
   * Hausaufgaben-Kachel für die Übersicht: immer aufgeklappt (kein
   * Chevron/Zuklappen mehr), Fach farbig am linken Rand. Klick auf die
   * Kachel öffnet das Thema, die Checkbox hakt ab.
   */
  function homeworkRow(hw: Homework): HTMLElement {
    const sub = subjectById(hw.subject);
    const accent = sub?.color ?? "";

    const preview = h("div", { class: "hw-preview hw-row-preview markdown-body" });
    renderPreview(preview, hw.text);
    // Aufgaben-Checkboxen im Text (`[ ]`) direkt abhakbar, ohne erst den
    // Bearbeiten-Dialog öffnen zu müssen.
    bindTaskCheckboxes(preview, () => hw.text, (next) => {
      hw.text = next;
      renderPreview(preview, hw.text);
      void homeworkService.update(hw.id, { text: next }).catch((err) => toast(pbErrorMessage(err), "error"));
    });

    const checkInput = h("input", { type: "checkbox", checked: hw.done }) as HTMLInputElement;
    checkInput.addEventListener("change", async () => {
      const done = checkInput.checked;
      Object.assign(hw, { done });
      try {
        await homeworkService.update(hw.id, { done });
        refresh();
      } catch (err) {
        toast(pbErrorMessage(err), "error");
        checkInput.checked = !done;
      }
    });
    const check = h("label", { class: "hw-tick", title: "Erledigt" }, checkInput, h("span", { class: "hw-tick-box", html: icon("check", 13) }));

    const name = h("span", { class: "hw-row-name" }, sub?.title ?? "Hausaufgabe");

    const editBtn = button({
      icon: "pencil",
      variant: "ghost",
      title: "Bearbeiten",
      onClick: () => openHomeworkEditor(hw, { title: sub?.title, onChanged: refresh }),
    });

    const row = h(
      "div",
      {
        class: "hw-row" + (hw.done ? " is-done" : ""),
        style: `--accent:${accent || "var(--text-muted)"}`,
        onclick: async (e: MouseEvent) => {
          // Haken und Bearbeiten-Knopf (label/button/input) lösen kein
          // Springen zum Thema aus.
          if ((e.target as HTMLElement).closest("button, input, label, a")) return;
          const goTo = (topicId: string) => navigate(`/topic/${topicId}`);
          if (hw.topic) goTo(hw.topic);
          else {
            const s = subjectById(hw.subject);
            const t = s ? await chooseTopic({ subject: s, topics: topics.filter((x) => x.subject === s.id) }) : undefined;
            if (t) goTo(t.id);
            else if (s) toast("Für dieses Fach gibt es noch kein Thema.", "info");
          }
        },
      },
      h("div", { class: "hw-row-head" }, check, name, h("span", { class: "spacer" }), editBtn),
      preview
    );
    return row;
  }

  /* ---------- Rendering ---------- */

  function render(): void {
    const base = filterSubjectId ? items.filter((hw) => hw.subject === filterSubjectId) : items;
    const open = base.filter((hw) => !hw.done);
    // Erledigt, aber Frist noch nicht vorbei: bleibt immer sichtbar (kein
    // Umschalten nötig). Erst nach der Frist verschwindet es standardmäßig.
    const notYetDueDone = base.filter((hw) => hw.done && (daysUntil(hw.dueDate) ?? 0) >= 0);
    const pastDueDone = base.filter((hw) => hw.done && (daysUntil(hw.dueDate) ?? 0) < 0);
    const visible = [...open, ...notYetDueDone, ...(showDone ? pastDueDone : [])].sort(
      (a, b) => a.dueDate.localeCompare(b.dueDate) || (a.duePeriod ?? 0) - (b.duePeriod ?? 0)
    );

    const clockBtn = button({
      icon: "clock",
      variant: "ghost",
      title: showDone ? "Erledigte (auch abgelaufene) ausblenden" : "Auch abgelaufene erledigte anzeigen",
      onClick: () => { showDone = !showDone; render(); },
    });
    if (showDone) clockBtn.classList.add("is-active");

    const filterSubject = filterSubjectId ? subjectById(filterSubjectId) : undefined;
    const header = h(
      "div",
      { class: "page-header" },
      h("h1", null, "Hausaufgaben"),
      filterSubject
        ? h(
            "button",
            {
              type: "button",
              class: "hw-filter-chip",
              title: "Filter aufheben",
              onclick: () => { location.hash = "/homework"; },
            },
            `(${filterSubject.title} ×)`
          )
        : null,
      h("span", { class: "spacer" }),
      clockBtn,
      voiceHomeworkPanel()
    );

    const groups: { key: string; rows: Homework[] }[] = [];
    for (const hw of visible) {
      const last = groups[groups.length - 1];
      if (last && last.key === hw.dueDate) last.rows.push(hw);
      else groups.push({ key: hw.dueDate, rows: [hw] });
    }

    const body = visible.length
      ? h(
          "div",
          { class: "hw-list" },
          ...groups.flatMap((g) => [
            h(
              "div",
              { class: "hw-group" },
              h("span", { class: "hw-group-day" }, dueLabel({ dueDate: g.key })),
              h("span", { class: "hw-group-rel" }, formatCountdown(g.key))
            ),
            ...g.rows.map(homeworkRow),
          ])
        )
      : h(
          "div",
          { class: "card card-pad" },
          emptyState({
            icon: "checklist",
            title: open.length ? "Nichts offen" : "Noch keine Hausaufgaben",
            text: open.length
              ? "Alles abgehakt – erledigte Aufgaben verschwinden, sobald ihre Frist vorbei ist."
              : "Trage oben ein, was zu tun ist – oder sag es per Mikrofon an.",
          })
        );

    createEl ??= createCard();
    mount(container, h("div", { class: "hw-page" }, header, createEl, body));
    const touch = window.matchMedia?.("(pointer: coarse)").matches ?? false;
    if (!focused && focusField && !touch) {
      focused = true;
      requestAnimationFrame(() => focusField?.focus());
    }
  }

  void load();
  return container;
}

/**
 * Bearbeiten-Dialog für eine Hausaufgabe. Liegt auf Modulebene, damit ihn auch
 * die Themenseite öffnen kann (Klick auf die Hausaufgaben-Karte dort).
 *
 * `onChanged` wird nach Speichern und nach Löschen gerufen – der Aufrufer lädt
 * damit seine Liste neu.
 */
export function openHomeworkEditor(
  hw: Homework,
  opts: { title?: string; onChanged?: () => void } = {}
): void {
  const termField = examTermField({
    subjectId: hw.subject,
    value: { date: hw.dueDate, periodFrom: hw.duePeriod, periodTo: hw.duePeriod },
    defaultMode: hw.duePeriod ? "slot" : "date",
  });

  const ta = h("textarea", {
    class: "textarea hw-editor",
    rows: 7,
    value: hw.text,
    placeholder: "Aufgabe … Tippe „/“ für Befehle (Aufgabenliste, Foto, Formel …).",
  }) as HTMLTextAreaElement;
  const mdField = textareaField(ta);

  const preview = h("div", { class: "hw-editor-preview markdown-body" });
  // Genau derselbe Vorschau-Pfad wie im großen Editor (markdown/preview.ts),
  // inklusive verzögerter Diagramme beim Tippen.
  const draw = () => renderPreview(preview, ta.value, { diagramDelay: 450 });
  draw();
  // Wie im großen Editor: Tippen debounced, direkte Aktionen (Haken-Klick
  // unten) lösen weiter sofort neu aus.
  let previewTimer: number | undefined;
  const drawDebounced = () => {
    window.clearTimeout(previewTimer);
    previewTimer = window.setTimeout(draw, 150);
  };
  ta.addEventListener("input", drawDebounced);
  bindTaskCheckboxes(preview, () => ta.value, (next) => {
    ta.value = next;
    draw();
  });

  const insertPhoto = () =>
    pickImage((file) => {
      toast("Lade Foto hoch …");
      void homeworkService
        .addPhoto(hw.id, file)
        .then((saved) => {
          const name = saved.photos[saved.photos.length - 1];
          insertAtCursor(mdField, `\n![Foto](${homeworkService.urlFor(saved, name)})\n`);
        })
        .catch((err) => toast(pbErrorMessage(err), "error"));
    });

  const openTable = () => {
    const block = findTableAt(ta.value, ta.selectionStart);
    openTableEditor({
      initial: block?.cells,
      meta: block?.meta,
      onSave: (md) => {
        mdField.focus();
        // Mit Formelzeile/Diagramm darunter, sonst bliebe sie als Rest stehen.
        if (block) mdField.setSelectionRange(block.start, block.endWithFormulas);
        mdField.insert(block ? md : `\n\n${md}\n\n`);
        mdField.emitInput();
      },
    });
  };

  // Exakt dieselben Bausteine wie im großen Editor, nur ohne dessen große
  // Werkzeugleiste. Reihenfolge zählt: solange das Slash-Menü offen ist,
  // verbraucht es Enter/Pfeiltasten vor der Listen-Fortführung.
  const slashMenu = attachSlashMenu(mdField, {
    cmds: SLASH_CMDS,
    onAction: (c) => {
      if (c.action === "table") return openTable();
      if (c.action === "term") return insertTermDef(mdField);
      if (c.action === "tree") return openTreeFor(mdField);
      insertPhoto();
    },
  });
  attachMarkdownKeys(mdField);

  const tools = h(
    "div",
    { class: "hw-tools-row" },
    button({ icon: "checklist", variant: "ghost", size: "sm", title: "Abhakbare Aufgabe", onClick: () => insertAtCursor(mdField, "\n[ ] ") }),
    button({ icon: "camera", variant: "ghost", size: "sm", title: "Foto", onClick: insertPhoto }),
    button({ icon: "table", variant: "ghost", size: "sm", title: "Tabelle", onClick: openTable }),
    h("span", { class: "spacer" }),
    h("span", { class: "muted text-sm" }, "„/“ für alle Befehle")
  );

  async function save(): Promise<void> {
    const term = termField.getValue();
    const text = ta.value.trim();
    handle.close();
    const data = {
      text,
      dueDate: term.date || hw.dueDate,
      duePeriod: term.periodFrom ?? null,
    } as Partial<Homework>;
    Object.assign(hw, data);
    try {
      await homeworkService.update(hw.id, data);
      toast("Hausaufgabe bearbeitet.", "success");
      opts.onChanged?.();
    } catch (err) {
      toast(pbErrorMessage(err), "error");
    }
  }

  async function removeWithConfirm(): Promise<void> {
    const ok = await confirmDialog({
      title: "Hausaufgabe löschen?",
      message: "Die Aufgabe wird samt Text und Fotos entfernt.",
      confirmLabel: "Löschen",
      danger: true,
    });
    if (!ok) return;
    try {
      await deleteHomework(hw);
      toast("Hausaufgabe gelöscht.", "success");
      opts.onChanged?.();
    } catch (err) {
      toast(pbErrorMessage(err), "error");
    }
  }

  const handle = openModal({
    title: opts.title ?? "Hausaufgabe",
    onClose: () => window.clearTimeout(previewTimer),
    body: h(
      "div",
      { class: "hw-editor-box" },
      field("Frist", termField.el),
      field("Aufgabe", h("div", null, tools, ta, preview)),
      slashMenu
    ),
    footer: h(
      "div",
      { class: "row" },
      button({
        icon: "trash",
        label: "Löschen",
        variant: "ghost",
        onClick: () => {
          handle.close();
          void removeWithConfirm();
        },
      }),
      h("span", { class: "spacer" }),
      button({ label: "Abbrechen", variant: "ghost", onClick: () => handle.close() }),
      button({ label: "Speichern", variant: "primary", onClick: () => void save() })
    ),
  });
}

/**
 * Die eine Hausaufgaben-Kachel der App: Haken, Kopfzeile, gerendertes
 * Markdown. Sie wird sowohl von der Hausaufgaben-Seite als auch vom
 * Hausaufgaben-Fenster der Themenansicht benutzt – Änderungen am Layout
 * wirken damit automatisch an beiden Stellen.
 *
 * Ein Klick auf die Kachel öffnet den Bearbeiten-Dialog; in die Themenansicht
 * führt allein der kleine Pfeil rechts.
 */
export function homeworkCard(
  hw: Homework,
  opts: {
    subjectTitle?: string;
    accent?: string;
    editing?: boolean;
    onChanged: () => void;
  }
): HTMLElement {
  const check = h("input", { type: "checkbox" }) as HTMLInputElement;
  check.checked = hw.done;
  check.addEventListener("change", () => {
    // Erst die Kachel selbst umschalten – sie blendet weich zurück (CSS).
    // Die Liste zieht erst danach nach, sonst würde der Neuaufbau die
    // Animation im ersten Bild abschneiden.
    card.classList.toggle("is-done", check.checked);
    void patch({ done: check.checked }, false);
    window.setTimeout(() => opts.onChanged(), 380);
  });
  const tick = h(
    "label",
    { class: "hw-tick", title: "Erledigt" },
    check,
    h("span", { class: "hw-tick-box", html: icon("check", 13) })
  );

  const preview = h("div", { class: "hw-preview markdown-body" });
  renderPreview(preview, hw.text);
  bindTaskCheckboxes(preview, () => hw.text, (next) => void patch({ text: next }, false));

  async function patch(data: Partial<Homework>, reload = true): Promise<void> {
    Object.assign(hw, data);
    try {
      await homeworkService.update(hw.id, data);
      if (reload) opts.onChanged();
    } catch (err) {
      toast(pbErrorMessage(err), "error");
    }
  }

  /** Sofort löschen (Stift-Modus) – die Rückfrage ersetzt der Modus selbst. */
  async function dropNow(): Promise<void> {
    try {
      await deleteHomework(hw);
      toast("Hausaufgabe gelöscht.", "success");
      opts.onChanged();
    } catch (err) {
      toast(pbErrorMessage(err), "error");
    }
  }

  /** Ohne festes Thema (Altbestand) entscheidet der gemeinsame Wähler. */
  async function openTopic(): Promise<void> {
    if (hw.topic) return navigate(`/topic/${hw.topic}`);
    const subject = await subjectsService.get(hw.subject).catch(() => undefined);
    const topic = subject ? await chooseTopic({ subject }) : undefined;
    if (topic) navigate(`/topic/${topic.id}`);
    else if (subject) toast("Für dieses Fach gibt es noch kein Thema.", "info");
  }

  const card = h(
    "div",
    {
      class: "hw-item" + (hw.done ? " is-done" : ""),
      style: `--accent:${opts.accent || defaultColor()}`,
    },
    tick,
    h(
      "div",
      {
        class: "hw-body is-clickable",
        title: "Hausaufgabe bearbeiten",
        onclick: (e: MouseEvent) => {
          // Haken, Links und andere Bedienelemente behalten Vorrang.
          if ((e.target as HTMLElement).closest("a, button, input, label, summary")) return;
          openHomeworkEditor(hw, { title: opts.subjectTitle, onChanged: opts.onChanged });
        },
      },
      h(
        "div",
        { class: "hw-head" },
        h("span", { class: "hw-subject" }, opts.subjectTitle ?? "Hausaufgabe"),
        hw.duePeriod ? h("span", { class: "hw-when" }, `${hw.duePeriod}. Std.`) : null
      ),
      preview
    ),
    h(
      "div",
      { class: "hw-tools" },
      h("button", {
        class: "hw-goto",
        type: "button",
        title: "Zur Themenseite",
        html: icon("link", 14),
        onclick: () => void openTopic(),
      }),
      opts.editing
        ? button({
            icon: "trash",
            variant: "ghost",
            size: "sm",
            title: "Löschen",
            onClick: () => void dropNow(),
          })
        : null
    )
  );
  return card;
}
